import { SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { ArrowLeftRight, FileText, Wallet } from 'lucide-react';
import { useWithdrawal } from '../hooks/useWithdrawal';
import { useWithdrawalFx } from '../hooks/useWithdrawalFx';
import { useAuth } from '@/auth/Auth';
import { Notice } from '@/components/shared/Notice';
import useModalStore from '@/store/useModalStore';
import useCurrencyStore, { type Currency } from '@/store/useCurrencyStore';
import WithdrawalAmountSlider, {
  MAX_WITHDRAWAL_LIMIT_CENTS,
} from './WithdrawalAmountSlider';
import WithdrawalCurrencyMode from './WithdrawalCurrencyMode';
import type { WithdrawalMode, WalletCurrency } from '../utils/withdrawalConversion';
import {
  resolveEffectiveMaxCents,
  capAmountToMax,
  isBelowUsdConversionMinimum,
} from '../utils/withdrawalConversion';
import { SecurityChallengeModal } from '@/components/shared/SecurityChallenge';



export default function WithdrawalForm({
  availableBalance,
  availableBalanceCents,
  currency,
  type,
  enabled = true,
  onClose,
}: {
  availableBalance: string;
  /**
   * Saldo disponível em centavos, vindo direto do valor decimal da API (não da string
   * formatada). Preferido sobre `availableBalance` quando presente — evita depender de
   * parse de string formatada como fonte de verdade para o saldo.
   */
  availableBalanceCents?: number;
  currency: string;
  type?: string;
  enabled?: boolean;
  onClose?: () => void;

}) {
  const { t, i18n } = useTranslation('wallet');
  const { closeModal } = useModalStore();
  const setCurrency = useCurrencyStore((s) => s.setCurrency);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [withdrawalMode, setWithdrawalMode] = useState<WithdrawalMode>('original');
  const [isChallengeOpen, setIsChallengeOpen] = useState(false);
  const [pendingData, setPendingData] = useState<{ amount_requested: string } | null>(null);

  const parsedAvailableBalanceInCents = availableBalance
    ? parseInt(availableBalance.replace(/[^\d,-]/g, '').replace(',', ''), 10)
    : 0;
  const availableBalanceInCents =
    typeof availableBalanceCents === 'number' && Number.isFinite(availableBalanceCents)
      ? availableBalanceCents
      : parsedAvailableBalanceInCents;

  const displayCurrency = typeof currency === 'string' ? currency : 'BRL';
  const isConverting = withdrawalMode === 'convert';
  const [currentValueInCents, setCurrentValueInCents] = useState(0);

  const {
    payoutCurrency,
    convertEnabled,
    referenceQuote,
    quote,
    isQuoteLoading,
    isQuoteError,
  } = useWithdrawalFx({
    enabled,
    walletCurrency: displayCurrency,
    isConverting,
    amountInCents: currentValueInCents,
  });

  const payoutCurrencyForSubmit = isConverting ? payoutCurrency : undefined;
  const { onSubmit, handleSubmit, control, errors, isLoading } = useWithdrawal(
    currency,
    setServerError,
    type,
    availableBalanceInCents,
    payoutCurrencyForSubmit,
    () => setIsChallengeOpen(true),
  );

  useEffect(() => {
    if (!convertEnabled) {
      setWithdrawalMode('original');
    }
  }, [convertEnabled]);

  useEffect(() => {
    setCurrentValueInCents(0);
    setWithdrawalMode('original');
    setServerError(null);
    setPendingData(null);
  }, [currency]);

  const platformMaxCents =
    MAX_WITHDRAWAL_LIMIT_CENTS[displayCurrency] ?? MAX_WITHDRAWAL_LIMIT_CENTS.BRL;
  const effectiveMaxCents = resolveEffectiveMaxCents(availableBalanceInCents, platformMaxCents);

  const MIN_AMOUNT_CENTS = displayCurrency === 'USD' ? 5000 : 10000;
  const minFormattedDisplay = displayCurrency === 'USD' ? 'US$ 50,00' : 'R$ 100,00';

  const isConvertingToUsd = isConverting && payoutCurrency === 'USD';
  const convertedAmountCents = isConvertingToUsd ? quote?.amount_converted ?? null : null;
  const belowUsdConvertMinimum = isBelowUsdConversionMinimum(
    type,
    isConvertingToUsd,
    convertedAmountCents,
  );

  const validationState = useMemo(() => {
    if (currentValueInCents === 0) return 'idle' as const;
    if (availableBalanceInCents <= 0) return 'no_balance' as const;
    if (currentValueInCents < MIN_AMOUNT_CENTS) return 'below_min' as const;
    if (belowUsdConvertMinimum) return 'below_min_usd' as const;
    if (currentValueInCents > availableBalanceInCents) return 'exceeds' as const;
    return 'valid' as const;
  }, [currentValueInCents, availableBalanceInCents, MIN_AMOUNT_CENTS, belowUsdConvertMinimum]);

  const resetAmount = (onChange: (value: string) => void) => {
    setCurrentValueInCents(0);
    onChange('0');
  };

  const handleSourceCurrencyChange = (
    nextCurrency: WalletCurrency,
    onChange: (value: string) => void,
  ) => {
    if (nextCurrency === displayCurrency) return;
    setServerError(null);
    setCurrency(nextCurrency as Currency);
    resetAmount(onChange);
  };

  const handleModeChange = (mode: WithdrawalMode) => {
    setServerError(null);
    setWithdrawalMode(mode);
  };

  const updateAmount = (
    valueInCents: number,
    onChange: (value: string) => void,
  ) => {
    const capped = capAmountToMax(valueInCents, effectiveMaxCents);
    setCurrentValueInCents(capped);
    onChange(capped.toString());
  };

  const submitWithChallenge = async (
    data: { amount_requested: string },
    challengeToken?: string,
  ) => {
    if (isSubmittingRef.current || isSubmitting) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await onSubmit(data, challengeToken);
    } catch (error) {
      console.error('Withdrawal error:', error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }

  };

  const handleFormSubmit = async (data: { amount_requested: string }) => {
    if (requires2FA) {
      setPendingData(data);
      setIsChallengeOpen(true);
      return;
    }
    await submitWithChallenge(data);
  };

  const fxBlocksSubmit = isConverting && (isQuoteLoading || isQuoteError || !referenceQuote);

  const isSubmitDisabled =
    isLoading ||
    isSubmitting ||
    fxBlocksSubmit ||
    validationState === 'idle' ||
    validationState === 'below_min' ||
    validationState === 'no_balance' ||
    validationState === 'exceeds' ||
    validationState === 'below_min_usd';

  const getInlineHint = () => {
    if (validationState === 'idle') return null;
    if (validationState === 'no_balance')
      return {
        text: t('withdrawal_validation.no_balance'),
        color: 'text-red-400',
      };

    if (validationState === 'below_min')
      return {
        text: t('withdrawal_validation.min_amount', {
          amount: minFormattedDisplay,
        }),
        color: 'text-red-400',
      };

    if (validationState === 'below_min_usd')
      return {
        text: t('withdrawal_validation.min_amount', {
          amount: 'US$ 50,00',
        }),
        color: 'text-red-400',
      };

    if (validationState === 'exceeds')
      return {
        text: t('withdrawal_validation.exceeds_balance'),
        color: 'text-red-400',
      };

    if (validationState === 'valid')
      return {
        text: t('withdrawal_validation.valid'),
        color: 'text-emerald-400',
      };
    return null;
  };

  const inlineHint = getInlineHint();
  const { authData } = useAuth();
  const requires2FA =
    (authData?.onboarding_step ?? '').toLowerCase() === 'finished';
  const isSubmittingRef = useRef(false);


  const fxErrorMessage =
    isConverting && isQuoteError
      ? t('withdrawal_fx.quote_error', {
        defaultValue: 'Não foi possível obter a cotação. Tente novamente.',
      })
      : null;

  const exchangeRateLabel = referenceQuote?.market_rate_label;



  const formTitle = isConverting
    ? t('withdrawal_convert.title', { defaultValue: 'Converter moeda' })
    : t('requestWithdrawal');

  const formSubtitle = isConverting
    ? t('withdrawal_convert.subtitle', {
      defaultValue:
        'Escolha a moeda, informe o valor e receba o dinheiro na sua conta bancária.',
    })
    : convertEnabled ? t('withdrawal_mode.original_subtitle', {
        defaultValue:
          'Informe o valor e receba o dinheiro na sua conta bancária, sem conversão.',
      })
      : null;
  

  return (
    <section className="flex flex-col p-0">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-0">
        <div className="p-6 space-y-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900">
              {isConverting ? (
                <ArrowLeftRight className="h-5 w-5 text-primary" />
              ) : (
                <Wallet className="h-5 w-5 text-primary" />
              )}
            </div>

            <div className="min-w-0">
              <SheetTitle className="text-left">{formTitle}</SheetTitle>
              {formSubtitle && (
                <p className="text-sm text-zinc-500 mt-1 leading-relaxed">{formSubtitle}</p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Wallet className="h-4 w-4 text-primary" />
            </div>

            <div className="min-w-0">
              <p className="text-xs text-zinc-500">
                {t('withdrawal_convert.available_balance', { defaultValue: 'Saldo disponível' })}
              </p>

              <p className="text-lg font-semibold text-white truncate">{availableBalance}</p>
            </div>
          </div>



          {convertEnabled && (
            <WithdrawalCurrencyMode
              walletCurrency={displayCurrency}
              mode={withdrawalMode}
              onModeChange={handleModeChange}
              convertEnabled={convertEnabled}
            />
          )}



          <Controller
            name="amount_requested"
            control={control}
            render={({ field }) => (
              <WithdrawalAmountSlider
                valueInCents={currentValueInCents}
                onChange={(cents) => {
                  setServerError(null);
                  updateAmount(cents, field.onChange);
                }}
                walletCurrency={displayCurrency}
                withdrawalMode={withdrawalMode}
                convertEnabled={convertEnabled}
                onSourceCurrencyChange={(nextCurrency) =>
                  handleSourceCurrencyChange(nextCurrency, field.onChange)
                }
                availableBalanceInCents={availableBalanceInCents}
                fxQuote={quote}
                isQuoteLoading={isConverting && isQuoteLoading}
                amountLabel={t('withdrawal_convert.amount_question', {
                  defaultValue: 'Qual valor quer sacar?',
                })}
                receivedLabel={t('withdrawal_convert.received_question', {
                  defaultValue: 'Quanto irá receber após conversão?',
                })}
              />
            )}
          />

          {isConverting && convertEnabled && (
            <div
              className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3"
              data-cy="taxa-cambio-saque"
            >
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-zinc-600 text-xs text-zinc-400">
                  i
                </span>

                <span>
                  {t('withdrawal_convert.exchange_rate', {
                    defaultValue: 'Taxa de câmbio comercial',
                  })}
                </span>
              </div>

              {isQuoteLoading ? (
                <span className="text-sm text-zinc-500">...</span>
              ) : exchangeRateLabel ? (
                <span className="text-sm font-medium text-primary whitespace-nowrap">
                  {exchangeRateLabel}
                </span>
              ) : (
                <span className="text-sm text-zinc-500">—</span>
              )}
            </div>
          )}



          {fxErrorMessage && (
            <p className="text-xs text-red-400" data-cy="erro-cotacao-saque">
              {fxErrorMessage}
            </p>

          )}



          {inlineHint && !errors.amount_requested && (
            <p
              className={`text-xs flex items-center gap-1 transition-all duration-200 ${inlineHint.color}`}
              data-cy="hint-valor-saque"
            >
              {validationState === 'valid' ? (
                <svg
                  className="w-3.5 h-3.5 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg
                  className="w-3.5 h-3.5 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z"
                  />
                </svg>
              )}
              {inlineHint.text}
            </p>
          )}


          {errors.amount_requested && (
            <p className="text-sm text-error" data-cy="erro-valor-saque">
              {errors.amount_requested.message}
            </p>
          )}
        </div>



        <div className="border-t p-6 border-zinc-800 space-y-4">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
            <div className="flex items-start gap-3">
              <FileText className="h-4 w-4 text-zinc-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-200 mb-1">
                  {t('withdrawal_terms.title')}
                </p>

                <p className="text-xs text-zinc-500 leading-relaxed">
                  {t('withdrawal_terms.description')}
                </p>

                <a
                  href={
                    i18n.language === 'pt'
                      ? 'https://mundpay.com.br/termos-de-uso'
                      : 'https://mundpay.com/termos-de-uso'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 mt-2 transition-colors"
                  data-cy="link-termos-saque"
                >
                  {t('withdrawal_terms.link_text')}
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>



          <Notice
            variant={serverError || fxErrorMessage ? 'error' : 'warning'}
            message={
              serverError
                ? serverError
                : fxErrorMessage
                  ? fxErrorMessage
                  : t('withdrawalDisclaimer', {
                    titular: authData?.name,
                  })
            }
          />



          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              data-cy="cancelar-saque"
              onClick={() => {
                if (onClose) {
                  onClose();
                } else {
                  closeModal('withdrawal-form');
                }
              }}
            >
              {t('cancel')}
            </Button>

            <Button
              type="submit"
              className="bg-primary text-black hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-200"
              data-cy="confirmar-saque"
              disabled={isSubmitDisabled}
            >
              {isLoading || isSubmitting ? t('processing') : t('confirmWithdrawal')}
            </Button>
          </div>
        </div>
      </form>

      <SecurityChallengeModal
        open={isChallengeOpen}
        purpose="withdrawal"
        email={authData?.email}
        metadata={{
          amount_requested: currentValueInCents,
          currency: displayCurrency as 'BRL' | 'USD' | 'EUR',
        }}
        onVerified={(token) => {
          setIsChallengeOpen(false);
          if (pendingData) {
            void submitWithChallenge(pendingData, token);
          }
        }}
        onCancel={() => setIsChallengeOpen(false)}
      />
    </section>
  );
}


